import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProgressBar from '../components/ProgressBar';
import ScrollToTop from '../components/ScrollToTop';

const CookiesPolicy = () => {
  const { isDark } = useDarkMode();

  const sections = [
    {
      title: '1. What Are Cookies',
      content: 'Cookies are small pieces of text stored on your device that help us recognize you when you visit our website. They allow us to remember your preferences, improve functionality, and enhance your browsing experience. Cookies may be permanent or temporary (session cookies that expire when you close your browser).'
    },
    {
      title: '2. Types of Cookies We Use',
      content: 'Essential Cookies: Required for the website to function properly, such as authentication and security cookies. Analytics Cookies: Help us understand how users interact with our website using tools like Google Analytics. Preference Cookies: Store your preferences such as language and theme settings. Marketing Cookies: Used to display relevant advertisements and track campaign performance.'
    },
    {
      title: '3. Google Analytics',
      content: 'We use Google Analytics to collect and analyze information about how you use our website. This includes pages visited, time spent, and referring websites. Google Analytics uses cookies and similar technologies to track and report website traffic. You can opt-out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on.'
    },
    {
      title: '4. Third-Party Cookies',
      content: 'Some cookies on our website are set by third-party services, including social media platforms, analytics providers, and advertising networks. These services have their own privacy policies and cookie practices. We recommend reviewing their policies to understand how they handle your information.'
    },
    {
      title: '5. Cookie Consent Banner',
      content: 'When you first visit our website, you will see a cookie consent banner. You can choose to accept all cookies, reject non-essential cookies, or manage your preferences. Your consent preferences are saved in a cookie and respected across subsequent visits.'
    },
    {
      title: '6. How to Manage Cookies',
      content: 'Most browsers allow you to manage cookies through their settings. You can choose to: accept all cookies, reject all cookies, or be asked before each cookie is set. Please note that disabling cookies may affect website functionality and your user experience. For more information, consult your browser\'s help documentation.'
    },
    {
      title: '7. Do Not Track Signals',
      content: 'Some browsers include a "Do Not Track" feature. Currently, there is no industry standard for recognizing Do Not Track signals. Media Society acknowledges such signals but does not currently respond to them. However, you can still manage cookies and tracking preferences through browser settings and our cookie consent tools.'
    },
    {
      title: '8. Cookie Retention',
      content: 'We retain cookies for as long as necessary to fulfill their purpose unless a longer retention period is required by law. Session cookies are automatically deleted when you close your browser. Persistent cookies may remain for several months or years depending on their function.'
    },
    {
      title: '9. International Transfers',
      content: 'Information collected via cookies may be transferred to, stored in, and processed in countries other than your country of residence. These countries may have data protection laws that differ from your home country. By using our website, you consent to such transfers.'
    },
    {
      title: '10. Updates to Cookie Policy',
      content: 'We may update this Cookies Policy from time to time to reflect changes in technology, legal requirements, or our practices. We will notify you of significant changes by posting the updated policy on this page and updating the "Last Updated" date.'
    },
    {
      title: '11. Contact Us for Cookie Questions',
      content: 'If you have questions about our use of cookies or would like to manage your cookie preferences, please contact us at: Email: ahtashamspeaks@gmail.com | Phone: 03047259307 | Address: Lahore, Pakistan'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className={`w-full transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-light-secondary'}`}>
      <ProgressBar />
      <ScrollToTop />
      <Navbar />

      <section className={`w-full py-20 lg:py-32 transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-light-secondary'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            className="mb-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
          >
            <motion.h1
              className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
                isDark ? 'text-white' : 'text-black'
              }`}
              variants={itemVariants}
            >
              Cookies Policy
            </motion.h1>
            <motion.p
              className={`font-poppins text-lg transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              variants={itemVariants}
            >
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </motion.p>
          </motion.div>

          {/* Content Sections */}
          <motion.div
            className="space-y-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`p-6 rounded-lg border transition-all duration-300 ${
                  isDark
                    ? 'bg-dark-secondary border-primary-gold/20 hover:border-primary-gold/40'
                    : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/40'
                }`}
              >
                <h2 className={`font-montserrat font-bold text-xl mb-3 transition-colors ${
                  isDark ? 'text-primary-gold' : 'text-primary-gold'
                }`}>
                  {section.title}
                </h2>
                <p className={`font-poppins leading-relaxed transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {section.content}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiesPolicy;
